import { ActivatedRoute } from '@angular/router'
import { Component, OnInit, OnDestroy } from '@angular/core'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import * as moment from 'moment-timezone'

import { Card, Organization, CardMembership } from '../models'
import { CardService } from '../services/card.service'
import { MembershipItem } from './models/membership-item'

@Component({
  standalone: false,
  selector: 'app-verify',
  templateUrl: './verify.component.html'
})
export class VerifyComponent implements OnInit, OnDestroy {

  public card: Card
  public legalOrProfileName: string
  public loaded = false
  public membershipItems: MembershipItem[] = []

  private currentDate: moment.Moment
  private organization: Organization
  private unsubscribe: Subject<any> = new Subject()

  public constructor(
    public acRoute: ActivatedRoute,
    private cardService: CardService
  ) { }

  public ngOnInit() {
    this.initRouteListener()
    this.initCard()
  }

  public ngOnDestroy() {
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }

  private initCard() {
    this.cardService.current
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((card: Card) => {
        if (card.memberships.length) {
          this.card = card
          this.legalOrProfileName = card.memberships[0].persona_eligibility.legal_or_profile_name
          this.organization = card.organization
          this.currentDate = moment.tz(this.organization.timezoneOrDefault)
          this.createMembershipItems()
        }
      })
  }

  private initRouteListener() {
    this.acRoute.params
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => this.cardService.getCard(data.cardId))
  }

  private relevantMembership(membership: CardMembership): boolean {
    const { cancel_date, expires_at } = membership
    const { timezoneOrDefault } = this.organization
    const expiresAt = moment.tz(expires_at, timezoneOrDefault)
    const hasTriggerCred = membership.persona_eligibility.eligibility_credentials.find((ec) =>
      ec.trigger && ec.is_most_relevant && ec.credential_value === membership.id
    )

    if (!hasTriggerCred || (expiresAt && this.currentDate.diff(expiresAt, 'days') > 30)) return false
    return !cancel_date || this.currentDate.diff(moment.tz(cancel_date, timezoneOrDefault), 'days') < 31
  }

  private async createMembershipItems(): Promise<void | boolean> {
    const { memberships, organization } = this.card
    const relevantMemberships = memberships.filter((membership) => this.relevantMembership(membership))
    if (!relevantMemberships.length) return this.loaded = true

    this.membershipItems = relevantMemberships
      .map((membership) => new MembershipItem({ membership, organization }))
      .sort(this.eligibleFirst)
    this.loaded = true
  }

  private eligibleFirst(a: MembershipItem, b: MembershipItem): number {
    const { eligibility_status: aStatus } = a.membership.credentialSet
    const { eligibility_status: bStatus } = b.membership.credentialSet
    const statuses = ['eligible', 'upcoming', 'ineligible']

    if (aStatus === bStatus) return 0

    for (let i = 0, status: string; i < 3; i++) {
      status = statuses[i]
      if (aStatus === status) return -1
      if (bStatus === status) return 1
    }
  }


}
